import type { AssetDescriptor } from './types';

// Utility functions for asset caching.
const queryOrHashRegex = /[?#]/;

export function normalizeUrlForId(url: string) {
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const u = new URL(url);
      return `${u.origin}${u.pathname}`;
    }
  } catch {
    // ignore invalid URLs
  }

  const [path] = url.split(queryOrHashRegex, 1);
  return path;
}

// Deterministic small hash to keep cache filenames stable.
export function stableHash(input: string) {
  let h = 0;
  for (const ch of input) {
    h = (Math.imul(31, h) + ch.charCodeAt(0)) % 2147483647;
  }
  return Math.abs(h).toString(36);
}

export function computeAssetId(url: string) {
  // CRITICAL: asset identity must depend on the exact original URL string.
  // Do not strip query params/fragments or normalize.
  return stableHash(url);
}

export function checkIsStaticUrl(url: string | undefined): url is string {
  if (typeof url !== 'string') return false;
  return url.startsWith('/') || url.startsWith('http://') || url.startsWith('https://');
}

// Resolution priority chain — mirrors the build-time prefetcher in
// `apps/matrix-native/scripts/utils/prefetchAndBundleAssets.ts` so that
// runtime-fetched assets and build-bundled assets land at the same
// extension. Load-bearing for loaders that path-sniff (lottie-react-
// native checks `.lottie`; PDF/video viewers check `.pdf` / `.mp4`).
//
//   1. URL extension     — `https://.../foo.lottie?v=2` → "lottie"
//   2. `fileType`        — from `AssetDescriptor.fileType` (upload metadata)
//   3. fallback          — "bin" (catch-all; Metro/expo-file-system both
//                          accept it, and our HTML-error-page guard at
//                          download time rejects mislabelled responses)
export function resolveExtension(descriptor: AssetDescriptor): string {
  const fromUrl = extractExtensionFromPath(descriptor.url);
  if (fromUrl) return fromUrl;

  if (descriptor.fileType) {
    const normalised = descriptor.fileType.toLowerCase().trim();
    if (normalised) return normalised;
  }

  return 'bin';
}

// Parses an SVG's `viewBox` ("minX minY W H", whitespace OR comma
// separated) and returns the W/H aspect ratio. Defaults to `1` when
// viewBox is missing or invalid — most SVGs in this codebase are
// icons (square). Shared between cache-time stash (AssetCacheManager)
// and render-time fallback (SVGImage when the entry pre-dates the
// aspect-ratio field).
export function parseSvgAspectRatio(xml: string): number {
  const match = xml.match(/viewBox=["']([\d.\s,-]+)["']/);
  if (!match?.[1]) return 1;
  const parts = match[1].split(/[\s,]+/).map(Number);
  const width = parts[2];
  const height = parts[3];
  if (!width || !height || width <= 0 || height <= 0) return 1;
  return width / height;
}

// Filesystem-safe basename derived from a descriptor's upstream
// `fileName`. Strips any trailing extension (the caller re-appends
// the resolved extension), replaces path separators / control chars
// with `_`, collapses runs of `_`, and caps at 64 chars so any FS
// accepts it. Returns `undefined` when the input is missing or
// yields an empty / hash-only result — the caller falls back to a
// plain `<assetId>.<extension>` path in that case.
//
// Shared between runtime (`AssetCacheManager.resolve`) and build-time
// (`prefetchAndBundleAssets`) so the on-disk filename pattern stays
// identical across both — same URL → same `<assetId>-<base>.<ext>`,
// which keeps the bundled-asset and runtime-cache outputs alignable.
export function sanitizeFileNameBase(fileName: string | undefined): string | undefined {
  if (!fileName) return undefined;
  const stripped = fileName.replace(/\.[A-Za-z0-9]{1,8}$/u, '');
  const safe = stripped
    .replace(/[^\w.\-\s]/gu, '_')
    .replace(/_{2,}/gu, '_')
    .replace(/\s+/gu, '_')
    .replace(/^[._-]+|[._-]+$/gu, '')
    .slice(0, 64);
  return safe || undefined;
}

// Pulls the file extension off any URL / path / filename string.
// Strips query + fragment first so `<host>/foo.png?v=2` still works.
// Caps at 8 chars to cover real extensions (`lottie`, `woff2`) while
// rejecting random dot-containing segments (e.g. a UUID `<uuid>.0`).
// Returns `undefined` when the input is missing or yields no
// recognisable extension. Shared between `resolveExtension`, the
// block-level extractor utilities, and the build-time prefetch.
export function extractExtensionFromPath(value?: string): string | undefined {
  if (!value) return undefined;
  const cleaned = value.split('?')[0]?.split('#')[0] ?? '';
  const lastDot = cleaned.lastIndexOf('.');
  if (lastDot === -1) return undefined;
  const ext = cleaned.slice(lastDot + 1).toLowerCase();
  if (!ext || ext.length > 8) return undefined;
  return ext;
}
